import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { apiFetch } from "../api/client";

const ROLE_LABELS = {
  PRIMARY_OWNER: { de: "Hauptverantwortlich", en: "Primary owner" },
  ADMIN: { de: "Administrator/in", en: "Administrator" },
  MEMBER: { de: "Mitglied", en: "Member" },
};

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString();
}

export default function AcceptInvitation() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.resolvedLanguage?.startsWith("en");
  const l = useCallback((de, en) => (isEnglish ? en : de), [isEnglish]);

  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState(null);
  const [loadError, setLoadError] = useState("");

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [submitStatus, setSubmitStatus] = useState("idle");
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await apiFetch(`/invitations/${token}`);
        if (cancelled) return;
        setInvitation(data.invitation);
        setFirstName(data.invitation?.first_name || "");
        setLastName(data.invitation?.last_name || "");
      } catch (err) {
        if (cancelled) return;
        setLoadError(err.message || l("Einladung nicht gefunden.", "Invitation not found."));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token, l]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus("loading");
    setSubmitError("");
    const body = {
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      preferredLanguage: isEnglish ? "en" : "de",
    };
    if (!invitation?.existing_user) {
      body.password = password;
    }
    try {
      await apiFetch(`/invitations/${token}/accept`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      setSubmitStatus("success");
      setTimeout(() => navigate("/login"), 1800);
    } catch (err) {
      setSubmitStatus("error");
      setSubmitError(
        err.message ||
          l("Einladung konnte nicht angenommen werden.", "Failed to accept invitation.")
      );
    }
  };

  const roleLabel = invitation
    ? ROLE_LABELS[invitation.role]?.[isEnglish ? "en" : "de"] || invitation.role
    : "";

  return (
    <div className="content">
      <Header />
      <div style={{ maxWidth: 480, margin: "40px auto", padding: "0 16px" }}>
        {loading && <p>{l("Lädt…", "Loading…")}</p>}

        {!loading && loadError && (
          <>
            <h2>{l("Einladung ungültig", "Invitation invalid")}</h2>
            <p style={{ color: "#a00" }}>{loadError}</p>
            <p>
              {l(
                "Bitte den Einladenden um eine neue Einladung bitten.",
                "Please ask the sender for a new invitation."
              )}
            </p>
          </>
        )}

        {!loading && !loadError && invitation && submitStatus === "success" && (
          <>
            <h2>
              {l("Willkommen!", "Welcome!")}
            </h2>
            <p>
              {l(
                `Du gehörst jetzt zum Unternehmen "${invitation.company_name}". Du wirst zum Login weitergeleitet…`,
                `You are now a member of "${invitation.company_name}". Redirecting you to the login…`
              )}
            </p>
          </>
        )}

        {!loading && !loadError && invitation && submitStatus !== "success" && (
          <>
            <h2>{l("Einladung annehmen", "Accept invitation")}</h2>
            <p>
              {l(
                <>du wurdest eingeladen, dem Unternehmen <strong>{invitation.company_name}</strong> als <strong>{roleLabel}</strong> beizutreten.</>,
                <>You have been invited to join <strong>{invitation.company_name}</strong> as <strong>{roleLabel}</strong>.</>
              )}
            </p>
            <p style={{ color: "#555", fontSize: 13 }}>
              {l("E-Mail-Adresse", "Email address")}: <strong>{invitation.email}</strong>
              {" · "}
              {l("Gültig bis", "Valid until")}: {formatDate(invitation.expires_at)}
            </p>

            <form className="login-form" onSubmit={handleSubmit} style={{ marginTop: 24 }}>
              {!invitation.existing_user && (
                <>
                  <input
                    placeholder={t("auth.register.firstNamePlaceholder", "Vorname")}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                  <input
                    placeholder={t("auth.register.lastNamePlaceholder", "Nachname")}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                  />
                  <input
                    type="password"
                    placeholder={t("auth.register.passwordPlaceholder", "Passwort")}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="new-password"
                    minLength={8}
                  />
                  <p style={{ fontSize: 12, color: "#666" }}>
                    {l(
                      "Mindestens 8 Zeichen, mit Großbuchstabe, Kleinbuchstabe und Zahl.",
                      "At least 8 characters, including uppercase, lowercase and a number."
                    )}
                  </p>
                </>
              )}
              {invitation.existing_user && (
                <p>
                  {l(
                    `Es existiert bereits ein Konto für ${invitation.email}. Klicke unten, um die Einladung diesem Konto hinzuzufügen.`,
                    `An account already exists for ${invitation.email}. Click below to add this invitation to your existing account.`
                  )}
                </p>
              )}

              <button
                className="form-button"
                type="submit"
                disabled={submitStatus === "loading"}
              >
                {submitStatus === "loading"
                  ? l("Verarbeite…", "Processing…")
                  : invitation.existing_user
                    ? l("Einladung annehmen", "Accept invitation")
                    : l("Konto erstellen und beitreten", "Create account and join")}
              </button>
            </form>

            {submitStatus === "error" && submitError && (
              <p style={{ color: "#a00", marginTop: 12 }}>{submitError}</p>
            )}
          </>
        )}
      </div>
      <Footer />
    </div>
  );
}
